export const DEFAULT_PIECES_PER_BUNDLE = 100;
/**
 * A trailing remainder smaller than this is folded into the previous bundle
 * rather than banded on its own, so the shop never makes a bundle of three.
 */
export const MIN_STANDALONE_REMAINDER = 20;
/**
 * Split a route's piece count into bundles.
 *
 * Invariant, for any `pieces > 0`:
 *
 *     (bundles - 1) * piecesPerBundle + lastBundlePieces === pieces
 *
 * Facing slips are printed one per bundle, so a discrepancy here means the
 * wrong number of slips physically go in the box. `generateFacingSlips`
 * duplicated this rule with a different fallback — `route.bundles ||
 * Math.ceil(pieces / 100)` and `(pieces % 100) || 100` — which disagreed with
 * this function whenever the remainder was under 20. For 810 pieces it printed
 * nine slips where the shop bands eight.
 */
export function calculateBundles(pieces, piecesPerBundle = DEFAULT_PIECES_PER_BUNDLE) {
    if (!Number.isFinite(pieces) || pieces <= 0) {
        return { bundles: 0, lastBundlePieces: 0 };
    }
    if (!Number.isFinite(piecesPerBundle) || piecesPerBundle <= 0) {
        throw new RangeError(`piecesPerBundle must be a positive number, got ${piecesPerBundle}`);
    }
    const whole = Math.floor(pieces / piecesPerBundle);
    const remainder = pieces % piecesPerBundle;
    // A small tail rides along with the last full bundle.
    if (remainder > 0 && remainder < MIN_STANDALONE_REMAINDER && whole > 0) {
        return { bundles: whole, lastBundlePieces: piecesPerBundle + remainder };
    }
    if (remainder > 0) {
        return { bundles: whole + 1, lastBundlePieces: remainder };
    }
    return { bundles: whole, lastBundlePieces: piecesPerBundle };
}
/**
 * Pieces in bundle `bundleNumber` (1-based) of a route.
 *
 * Used when printing facing slips, which state the piece count of the specific
 * bundle they are banded to.
 */
export function piecesInBundle(bundleNumber, pieces, piecesPerBundle = DEFAULT_PIECES_PER_BUNDLE) {
    const { bundles, lastBundlePieces } = calculateBundles(pieces, piecesPerBundle);
    if (bundleNumber < 1 || bundleNumber > bundles) {
        throw new RangeError(`bundle ${bundleNumber} does not exist; route has ${bundles}`);
    }
    return bundleNumber === bundles ? lastBundlePieces : piecesPerBundle;
}
//# sourceMappingURL=bundles.js.map